/**
 * Prototype-only mock playback for the /execute Execution Logs.
 *
 * Source of truth for the log lines below:
 *   /Users/wei/coding_claude/FederateX_agent_sync/Backend_To_Frontend/
 *     2026-05-25_training_progress_mock_server_side_only_v2.md
 *
 * Boundary — explicitly server-side-only:
 *   - 🅵 lines are Flower framework output that ServerApp surfaces.
 *   - ✅ lines are ServerApp / Tier1 exporter prints from the backend server side.
 *   - ⚠ lines are frontend-synthesized UI status (no backend line backs them).
 *
 *   NO ClientApp pod stdout / stderr is present. No per-epoch train/val loss,
 *   no GPU banner, no dataset path loading, no `[ProstateX seg] dataset sizes`,
 *   no `[!!!!!!client tabular]`, no `Configuration Setting as follow`, no
 *   `[part] chunk_X`. Those will be the site-authorized debug view in a later
 *   phase — out of scope here.
 *
 * This file is mock-only: no API calls, no WebSocket, no SSE, no real stream.
 */

// ─── Tag classifier ────────────────────────────────────────────────────────
// Three sources only. No ClientApp tag.
export const LOG_TAGS = {
  flower: { symbol: "🅵", source: "Flower" },
  server: { symbol: "✅", source: "Server" },
  ui:     { symbol: "⚠",  source: "UI" },
};

// Map a playback tag to the `level` field on the existing addLog signature.
// ExecutionLogsCard colours by level: info=cyan, success=green, warning=amber.
const tagToLevel = (tag) => {
  switch (tag) {
    case "server": return "success";    // ServerApp side prints → green
    case "ui":     return "success";    // UI completion banner → green
    case "flower":
    default:       return "info";       // Flower framework → cyan
  }
};

/** Format a log line for display. Keeps the tag glyph at the front so the
 *  terminal-style ExecutionLogsCard still reads like a real flwr stream. */
const formatLine = (tag, text) => {
  const symbol = LOG_TAGS[tag]?.symbol || "";
  return symbol ? `${symbol}  ${text}` : text;
};

// ─── Header / per-round / tier1 / summary builders ─────────────────────────
//
// Templates render against `numRounds` so the user's `num-server-rounds` choice
// drives the per-round playback length. SUMMARY history dicts are illustrative
// (per the source doc, only imaging-cls 5×3 and seg 10-round headline metrics
// are real; tabular SUMMARY values are explicitly illustrative).

const padId = () => Math.floor(Math.random() * 1e19).toString();

const baseHeader = (numRounds, runId) => [
  { tag: "server", text: "Loading project configuration..." },
  { tag: "server", text: "Success" },
  { tag: "flower", text: `🎊 Successfully started run ${runId}` },
  { tag: "flower", text: `INFO :      Starting logstream for run_id \`${runId}\`` },
  { tag: "flower", text: "INFO :      Start `flwr-serverapp` process" },
  { tag: "flower", text: "🎊 Successfully installed pytorchexample to /home/appuser/.flwr/apps/flwrlabs.pytorchexample.1.0.0.<FAB_HASH>.fab" },
];

const startServerAppLine = (numRounds) => ({
  tag: "flower",
  text: `INFO :      Starting Flower ServerApp, config: num_rounds=${numRounds}, no round_timeout`,
});

const initBlock = () => [
  { tag: "flower", text: "INFO :      [INIT]" },
  { tag: "flower", text: "INFO :      Requesting initial parameters from one random client" },
  { tag: "flower", text: "INFO :      Received initial parameters from one random client" },
  { tag: "flower", text: "INFO :      Starting evaluation of initial global parameters" },
  { tag: "flower", text: "INFO :      Evaluation returned no results (`None`)" },
];

const roundBlockFlower = (n) => [
  { tag: "flower", text: `INFO :      [ROUND ${n}]` },
  { tag: "flower", text: "INFO :      configure_fit: strategy sampled 2 clients (out of 2)" },
  { tag: "flower", text: "INFO :      aggregate_fit: received 2 results and 0 failures" },
  { tag: "flower", text: "INFO :      configure_evaluate: strategy sampled 2 clients (out of 2)" },
  { tag: "flower", text: "INFO :      aggregate_evaluate: received 2 results and 0 failures" },
];

const flTaskLine = (n, total) => ({
  tag: "server",
  text: `[FL_TASK] ROUND ${n}/${total}`,
});

// ─── Pipeline templates ────────────────────────────────────────────────────

const buildTabular = (numRounds) => {
  const runId = padId();
  const lines = [
    ...baseHeader(numRounds, runId),
    startServerAppLine(numRounds),
    ...initBlock(),
  ];
  for (let n = 1; n <= numRounds; n += 1) {
    lines.push(...roundBlockFlower(n));
  }
  lines.push(
    { tag: "server", text: "[Tabular Tier1 exporter] Tier1 JSON: runs/tabular_tier1/Tier1_result_summary.json" },
    { tag: "server", text: "[Tabular Tier1 exporter] Metrics over rounds: runs/tabular_tier1/metrics_over_rounds.json" },
    { tag: "server", text: "[Tabular Tier1 exporter] HTML card: runs/tabular_tier1/Tier1_result_card.html" },
    { tag: "flower", text: "INFO :      [SUMMARY]" },
    { tag: "flower", text: `INFO :      Run finished ${numRounds} round(s) in ${(numRounds * 9.6).toFixed(2)}s` },
    { tag: "flower", text: "INFO :        History (loss, distributed):" },
  );
  // Illustrative loss curve: 1.82 → 1.55 over 4 rounds, interpolated linearly.
  for (let n = 1; n <= numRounds; n += 1) {
    const loss = (1.82 - ((1.82 - 1.55) * (n - 1)) / Math.max(numRounds - 1, 1)).toFixed(2);
    lines.push({ tag: "flower", text: `INFO :                round ${n}: ${loss}` });
  }
  return {
    pipelineKey: "tabular",
    pipelineLabel: "Tabular",
    numRounds,
    lines,
  };
};

const buildImagingClassification = (numRounds) => {
  const runId = padId();
  const lines = [
    ...baseHeader(numRounds, runId),
    startServerAppLine(numRounds),
    ...initBlock(),
  ];
  for (let n = 1; n <= numRounds; n += 1) {
    lines.push(...roundBlockFlower(n));
  }
  lines.push(
    { tag: "server", text: "[Imaging Tier1 exporter] Tier1 JSON: runs/imaging_tier1/Tier1_result_summary.json" },
    { tag: "server", text: "[Imaging Tier1 exporter] Metrics over rounds: runs/imaging_tier1/metrics_over_rounds.json" },
    { tag: "server", text: "[Imaging Tier1 exporter] HTML card: runs/imaging_tier1/Tier1_result_card.html" },
    { tag: "flower", text: "INFO :      [SUMMARY]" },
    { tag: "flower", text: `INFO :      Run finished ${numRounds} round(s) in 1842.36s` },
    { tag: "flower", text: "INFO :        History (metrics, distributed, evaluate):" },
    // Real M3.C 5×3 PASS anchors — accuracy/f1/roc_auc final values.
    { tag: "flower", text: "INFO :        {'accuracy': [(1, 0.2833), (2, 0.7167), (3, 0.8267), (4, 0.8800), (5, 0.8900)]," },
    { tag: "flower", text: "INFO :         'f1':       [(1, 0.1245), (2, 0.6483), (3, 0.7821), (4, 0.8157), (5, 0.8193)]," },
    { tag: "flower", text: "INFO :         'roc_auc':  [(1, 0.5421), (2, 0.7842), (3, 0.8754), (4, 0.9087), (5, 0.9175)]}" },
  );
  return {
    pipelineKey: "imaging_classification",
    pipelineLabel: "Imaging Classification",
    numRounds,
    lines,
  };
};

const buildImagingSegmentation = (numRounds) => {
  const runId = padId();
  const lines = [
    ...baseHeader(numRounds, runId),
    // ServerApp banner is unique to segmentation.
    { tag: "server", text: "[ServerApp] segmentation_dataset_subtype='prostatex', deferring initial parameters to client round 0." },
    startServerAppLine(numRounds),
    ...initBlock(),
  ];
  for (let n = 1; n <= numRounds; n += 1) {
    lines.push(...roundBlockFlower(n));
    lines.push(flTaskLine(n, numRounds));
  }
  lines.push(
    { tag: "server", text: "[Seg Tier1 exporter] Tier1 JSON: runs/seg_tier1/Tier1_result_summary.json" },
    { tag: "server", text: "[Seg Tier1 exporter] Metrics over rounds: runs/seg_tier1/metrics_over_rounds.json" },
    { tag: "server", text: "[Seg Tier1 exporter] HTML card: runs/seg_tier1/Tier1_result_card.html" },
    { tag: "flower", text: "INFO :      [SUMMARY]" },
    { tag: "flower", text: `INFO :      Run finished ${numRounds} round(s) in 1866.63s` },
    { tag: "flower", text: "INFO :        History (loss, distributed):" },
    { tag: "flower", text: "INFO :                round 1: 5.0755" },
    { tag: "flower", text: `INFO :                round ${numRounds}: 0.8341` },
    { tag: "flower", text: "INFO :        History (metrics, distributed, evaluate):" },
    // Real ProstateX A1 10-round anchors.
    { tag: "flower", text: "INFO :        {'val_dice_hard': [(1, 0.0943), ..., (10, 0.7031)]," },
    { tag: "flower", text: "INFO :         'val_dice_soft': [(1, 0.0927), ..., (10, 0.6959)]," },
    { tag: "flower", text: "INFO :         'val_iou_hard':  [(1, 0.0495), ..., (10, 0.5422)]}" },
  );
  return {
    pipelineKey: "imaging_segmentation",
    pipelineLabel: "Imaging Segmentation",
    numRounds,
    lines,
  };
};

const buildGenericFallback = (numRounds) => ({
  pipelineKey: "generic",
  pipelineLabel: "Generic",
  numRounds,
  lines: [
    { tag: "server", text: "Loading project configuration..." },
    { tag: "server", text: "Success" },
    { tag: "flower", text: "INFO :      Starting Flower ServerApp..." },
    { tag: "flower", text: "INFO :      [ROUND 1]" },
    { tag: "flower", text: "INFO :      aggregate_fit: received 2 results and 0 failures" },
    { tag: "flower", text: "INFO :      aggregate_evaluate: received 2 results and 0 failures" },
    { tag: "flower", text: "INFO :      [SUMMARY]" },
  ],
});

// Default round counts per pipeline (match the anchor doc's examples).
const DEFAULT_ROUNDS = {
  tabular: 4,
  imaging_classification: 5,
  imaging_segmentation: 10,
};

/**
 * Pick the log template that fits the user's selection.
 *
 *  @param {object} args
 *  @param {string=} args.processingType  one of tabular / imaging_classification /
 *                                         imaging_segmentation / segmentation /
 *                                         imaging
 *  @param {string=} args.imagingTask     used as a secondary hint when
 *                                         processingType is just "imaging"
 *  @param {number=} args.numRounds       user-configured num_rounds, falls back
 *                                         to the per-pipeline default
 */
export const selectLogTemplate = ({ processingType, imagingTask, numRounds } = {}) => {
  const norm = (processingType || "").toString().toLowerCase();
  const task = (imagingTask || "").toString().toLowerCase();

  if (norm === "tabular") {
    return buildTabular(numRounds || DEFAULT_ROUNDS.tabular);
  }
  if (norm === "imaging_classification" || (norm === "imaging" && task === "classification")) {
    return buildImagingClassification(numRounds || DEFAULT_ROUNDS.imaging_classification);
  }
  if (
    norm === "imaging_segmentation" ||
    norm === "segmentation" ||
    (norm === "imaging" && task === "segmentation")
  ) {
    return buildImagingSegmentation(numRounds || DEFAULT_ROUNDS.imaging_segmentation);
  }
  return buildGenericFallback(numRounds || 1);
};

/** Estimate the round number a given line "belongs" to so the playback can
 *  push progress / round state in lock-step with the visible lines. */
const extractRoundFromText = (text) => {
  const m = /\[ROUND (\d+)\]/.exec(text || "");
  if (m) return parseInt(m[1], 10);
  const m2 = /\[FL_TASK\] ROUND (\d+)\/\d+/.exec(text || "");
  if (m2) return parseInt(m2[1], 10);
  return null;
};

/** Detect the line that marks a round's COMPLETION on the server side.
 *  Each round's lifecycle ends with `aggregate_evaluate: received N results`
 *  — at that point all clients have submitted both fit and evaluate results
 *  for the round, so it's safe to advance the progress bar. */
const isRoundCompletionLine = (text) =>
  /aggregate_evaluate: received \d+ results/.test(text || "");

const randomDelayMs = () => 1000 + Math.floor(Math.random() * 2000); // 1000-3000

/**
 * Start streaming a template's lines via `addLog`. Returns a cancel handle.
 *
 *  Round timing semantics:
 *    - `onRoundChange(N, total)` fires when the log emits `[ROUND N]` —
 *      this advances the "current round being processed" indicator.
 *    - The progress bar (`onProgress`) advances on `aggregate_evaluate`
 *      lines, i.e. when round N actually FINISHES on the server side.
 *      So a 2-round run goes 0 → 50% (after round 1 ends) → 100% (after
 *      round 2 ends). Init lines stay at 0%, post-rounds Tier1 / SUMMARY
 *      lines hold at 100%.
 *    - `onAllRoundsDone()` fires exactly once when the last round's
 *      aggregate_evaluate ticks — the caller can flip status to
 *      "completed" at that moment even though more SUMMARY / footer
 *      lines will still stream into the log panel afterwards.
 *
 *  @param {object} args
 *  @param {object} args.template          from selectLogTemplate
 *  @param {(message:string, level:string)=>void} args.addLog
 *  @param {(p:number)=>void=} args.onProgress  0-100 ticks
 *  @param {(round:number, total:number)=>void=} args.onRoundChange   currently-processing round
 *  @param {(clients:number)=>void=} args.onClientsChange
 *  @param {()=>void=} args.onAllRoundsDone  fires once on last round's aggregate_evaluate
 *  @param {()=>void=} args.onComplete       fires at the very end of the stream
 *  @param {(line:{tag:string, text:string})=>void=} args.onLine  raw per-line hook
 *  @param {{min?:number, max?:number}=} args.interval  override default 1–3s
 *  @returns {{cancel:()=>void, isRunning:()=>boolean}}
 */
export const playMockTraining = ({
  template,
  addLog,
  onProgress,
  onRoundChange,
  onClientsChange,
  onAllRoundsDone,
  onComplete,
  onLine,
  interval,
} = {}) => {
  if (!template || !Array.isArray(template.lines) || template.lines.length === 0) {
    onComplete && onComplete();
    return { cancel: () => {}, isRunning: () => false };
  }

  let idx = 0;
  let cancelled = false;
  let timer = null;
  const total = template.lines.length;
  const totalRounds = template.numRounds || 1;
  let lastRoundEmitted = 0;     // round currently being processed
  let lastRoundCompleted = 0;   // last round whose aggregate_evaluate fired
  let allDoneFired = false;
  let clientsAnnounced = false;

  const pickDelay = () => {
    const min = interval?.min ?? 1000;
    const max = interval?.max ?? 3000;
    return min + Math.floor(Math.random() * Math.max(1, max - min + 1));
  };

  const tick = () => {
    if (cancelled) return;
    if (idx >= total) {
      // UI-synthesized completion footer. ⚠ tag.
      addLog && addLog(formatLine("ui", "Training completed successfully."), tagToLevel("ui"));
      addLog &&
        addLog(
          formatLine(
            "ui",
            `Final Flower progress: round ${totalRounds}/${totalRounds}, 100%`
          ),
          tagToLevel("ui")
        );
      onProgress && onProgress(100);
      onComplete && onComplete();
      return;
    }
    const line = template.lines[idx];
    if (line && line.text && line.text.trim().length > 0) {
      const formatted = formatLine(line.tag, line.text);
      addLog && addLog(formatted, tagToLevel(line.tag));
      onLine && onLine(line);
    }

    // Drive client count from the first `configure_fit` we see.
    if (!clientsAnnounced && /configure_fit: strategy sampled (\d+) clients/.test(line?.text || "")) {
      const m = /configure_fit: strategy sampled (\d+) clients/.exec(line.text);
      const clients = parseInt(m[1], 10);
      onClientsChange && onClientsChange(clients);
      clientsAnnounced = true;
    }

    // Track the currently-processing round from [ROUND N] / [FL_TASK].
    // Does NOT move the bar — only the "Round N/M" indicator.
    const roundN = extractRoundFromText(line?.text);
    if (roundN && roundN !== lastRoundEmitted) {
      onRoundChange && onRoundChange(roundN, totalRounds);
      lastRoundEmitted = roundN;
    }

    // Round completion = aggregate_evaluate received. THIS is what advances
    // the progress bar — round work is actually done at this point.
    if (
      isRoundCompletionLine(line?.text) &&
      lastRoundEmitted > lastRoundCompleted
    ) {
      lastRoundCompleted = lastRoundEmitted;
      if (lastRoundCompleted >= totalRounds && !allDoneFired) {
        allDoneFired = true;
        // Fire the "all rounds done" signal. The caller flips status to
        // "completed" here so the UI doesn't sit at "in progress" while
        // Tier1 exporter / SUMMARY lines continue to stream below.
        onAllRoundsDone && onAllRoundsDone();
      }
    }

    // Bar reflects completed rounds. Stays at 0 until the FIRST
    // aggregate_evaluate; stays at 100 once the last round's
    // aggregate_evaluate fires (including during the trailing Tier1 /
    // SUMMARY / footer lines).
    const pct =
      lastRoundCompleted === 0
        ? 0
        : Math.min(
            100,
            Math.round((lastRoundCompleted / Math.max(1, totalRounds)) * 100)
          );
    onProgress && onProgress(pct);

    idx += 1;
    timer = setTimeout(tick, pickDelay());
  };

  // Kick off immediately so the user sees the first line right after Start.
  timer = setTimeout(tick, 60);

  return {
    cancel: () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },
    isRunning: () => !cancelled && idx < total,
  };
};
